// @ts-ignore
import TWEEN from '@tweenjs/tween.js';
// todo: Remove this polyfill when minimum supported version of Safari has native ResizeObserver.
import ResizeObserver from 'resize-observer-polyfill';
import * as THREE from 'three';
import { Box2, Scene, WebGLRenderer } from 'three';
import { CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer';
import { CSS3DRenderer } from 'three/examples/jsm/renderers/CSS3DRenderer';
import _ from 'underscore';

import { FindProjectModel } from '../../../typings/api/skymap/rest/v0/.common';
import { PoiLabelManager } from '../react/components/skyview-new/asset-menu/components/label-renderer/labels/poi-labels/poi-label-manager';
import { wmsCanvasRenderedEvent } from '../react/components/skyview-new/components/wms-skyview-bridge';
import { ThemeManager } from '../react/themes/theme-manager';
import { waitForElement } from '../utils/dom/mutation-observer';
import { isDefined } from '../utils/variables';
import { AssetManager } from './asset-manager';
import cameraManager from './camera-manager';
import { CameraType } from './cameras/types';
import { CoordinateManager } from './coordinate-manager';
import { FpsCounter } from './fps-counter';
import { AsbuiltGeometry } from './geodata/asbuilt-geometry/asbuilt-geometry';
import { Cesium3dTilesPointsMaterial } from './geodata/point-clouds/cesium-3d-tiles-point-cloud-renderer/cesium-3d-tiles-points-material';
import { CesiumPointsMaterial } from './geodata/point-clouds/cesium-point-cloud/points/material/cesium-point-material';
import { PointCloudHandle } from './geodata/point-clouds/point-cloud-handle';
import { InputHandler } from './input-handler';
import { publish, SubscriptionTopic } from './messaging/pubsub';
import { ProjectGeodataManager } from './project-geodata-manager';
import { SceneManager, SceneManagerOptions } from './scene-manager';
import { SkyviewScene } from './scenes/skyview-scene';
import state from './state';
import { SunManager } from './sun-manager';
import { Point2d } from './utilities/math-helpers';
import { OctTree } from './utilities/octTree/octTree';
import { TextureManager } from './utilities/texture-manager/texture-manager';
import { ClassicView, ViewSettings } from './view-settings';
import { WmsState } from './wms-state';

export interface SkyViewOptions {
  sceneManagerOptions?: SceneManagerOptions;
  viewerElement?: HTMLElement;
}

const stateValues: any = {
  view_bounding_box: true,
  view_pointcloud: false,
};

// Define custom event map for SkyView
type SkyViewEventMap = {
  wmsCanvasRendered: {
    data?: {
      canvas: HTMLCanvasElement;
      extents: Box2;
    };
  };
  canvasResize: {
    width: number;
    height: number;
  };
  // eslint-disable-next-line @typescript-eslint/ban-types
  updateUI: {};
};

export class SkyView extends THREE.EventDispatcher<SkyViewEventMap> {
  octtree?: OctTree;
  baseScene: Scene;
  options: SkyViewOptions;
  viewerElement?: HTMLElement;
  gltftScene?: Scene;
  deactivated: boolean;

  project?: FindProjectModel;
  FPSCounter: FpsCounter;
  lastUpdate: number;
  canvas?: HTMLCanvasElement;
  animateCallback?: (event: THREE.BaseEvent<'animate'>) => void;
  updateUICallback?: (event: THREE.BaseEvent<'updateUI'>) => void;
  inputHandler?: InputHandler;

  resizeObserver?: ResizeObserver;
  animateInterval?: NodeJS.Timeout;
  updateUIInterval?: NodeJS.Timeout;

  get renderer(): WebGLRenderer | undefined {
    return this._renderer;
  }
  set renderer(value: WebGLRenderer) {
    this._renderer = value;
  }

  // This is a more intuitive place for the function (easier to find than in ThemeManager),
  // but keeping the data source in existing place.
  // Preferably, refactor away from ThemeManager.
  get sharedView(): boolean {
    return ThemeManager.instance.isSharedView;
  }

  _renderer?: WebGLRenderer;
  css2dRenderer?: CSS2DRenderer;
  css3dRenderer?: CSS3DRenderer;
  wgs84Position?: { lat: number; lng: number; alt: number };
  updatedScreenRatio?: Point2d;
  pushLoadingState: any;
  occlusionCulling = false;
  resizeObserverTarget?: Element;

  readonly asbuiltGeometry: AsbuiltGeometry = new AsbuiltGeometry();

  constructor(project?: FindProjectModel, options?: SkyViewOptions) {
    super();

    PoiLabelManager.instance.cleanup();

    this.octtree = undefined;
    this.baseScene = new THREE.Scene();
    this.options = options || {};
    this.viewerElement = options?.viewerElement;
    this.gltftScene = undefined;
    this.deactivated = false;

    this.project = project;

    this.FPSCounter = new FpsCounter();

    this.lastUpdate = Date.now();
  }

  async init() {
    await this.fetchTopViewerContainer();

    this.animateCallback = this.animate.bind(this);
    this.updateUICallback = this.updateUI.bind(this);

    this.inputHandler = InputHandler.instance;

    this.baseScene.add(this.inputHandler);

    this.baseScene.add(this.asbuiltGeometry);

    this.setOcclusionCulling(true);

    setTimeout(() => {
      if (!this.deactivated) {
        const renderUpdate = (state.getValue('renderFPS') as number | undefined) ?? 30;
        this.animateInterval = setInterval(
          () => InputHandler.instance.dispatchEvent({ type: 'animate' }),
          1000 / renderUpdate,
        );
        this.updateUIInterval = setInterval(
          () => InputHandler.instance.dispatchEvent({ type: 'updateUI' }),
          1000 / renderUpdate,
        );
      }
    }, 500);

    window.setTimeout(() => {
      if (!this.deactivated) {
        this.animate();
      }
    }, 1000);

    SunManager.instance.setDirection(0);

    cameraManager().init();

    SceneManager.instance.init(this.options && this.options.sceneManagerOptions);

    if (!this.deactivated) {
      this.setupRenderer();
      this.setupResizeObserver();
      this.resizeCanvas(window.innerWidth, window.innerHeight);

      InputHandler.instance.reset(this.renderer!.domElement);
      InputHandler.instance.setScene(this.baseScene);
      await CesiumPointsMaterial.instance.initialize();
      await Cesium3dTilesPointsMaterial.instance.initialize();

      // SkyView is shown once its scene has opened; the timeline data then loads on its own
      try {
        await SceneManager.instance.setScene('skyviewScene');
      } catch (err) {
        publish(SubscriptionTopic.SkyviewLoadingProgressError, {});
        throw err;
      }
    }

    // Re-check after the async setScene — clearCustom() may have been called during the await,
    // which sets deactivated=true and skips setProjectProjectionData, leaving projectProjection
    // as '' and causing WmsState.initialize() to fail.
    if (this.deactivated) {
      return;
    }

    WmsState.instance.initialize();

    InputHandler.instance.setTool('select');
    InputHandler.instance.addEventListener('animate', this.animateCallback);
    InputHandler.instance.addEventListener('updateUI', this.updateUICallback);

    this.addEventListener(wmsCanvasRenderedEvent, this.updateWmsTexture.bind(this));
  }

  setupRenderer() {
    const css3dRendererContainer = document.getElementById('css3drenderercontainer');
    const webGlRendererContainer = document.getElementById('webglrenderercontainer');

    if (!isDefined(css3dRendererContainer) || !isDefined(webGlRendererContainer)) {
      throw Error('Error setting up renderer. Container not found.');
    }

    css3dRendererContainer.childNodes.forEach((x) => x.remove());
    webGlRendererContainer.childNodes.forEach((x) => x.remove());

    // The stencil lets a translucent orthophoto draw each pixel from one tile only, see OrthoTileAsset
    this.renderer = new WebGLRenderer({
      antialias: false,
      preserveDrawingBuffer: true,
      stencil: true,
    });
    this.css2dRenderer = new CSS2DRenderer();
    this.css3dRenderer = new CSS3DRenderer();
    this.canvas = this.renderer.domElement;

    css3dRendererContainer.appendChild(this.css2dRenderer.domElement);
    css3dRendererContainer.appendChild(this.css3dRenderer.domElement);
    webGlRendererContainer.appendChild(this.renderer.domElement);
  }

  /**
   * Sets up a resize observer for the viewer DOM element.
   * This element resizes when menu collapses/expands or
   * when window is resized.
   */
  setupResizeObserver(container?: HTMLElement) {
    const target = container ?? this.viewerElement ?? document.getElementById('viewer');
    if (!target) {
      throw new Error('Error setting up renderer. Viewer container not found.');
    }

    const throttle = _.throttle(
      (width: number, height: number) => {
        this.resizeCanvas(width, height);
      },
      50,
      { leading: true },
    );

    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        const height = entry.contentRect.height;
        throttle(width, height);
      }
    });

    this.resizeObserver.observe(target);
    this.resizeObserverTarget = target;
  }

  /**
   * @description Sets the active container in a scene and reloads with the new data
   */
  async setActiveContainer(timelineContainerIdOrIndex: string | number) {
    // Store the current world coordinates for the camera when changin the timeline container
    cameraManager().updateGeographicCoords();

    PubSub.publish('onTimelineLoadStart', {});
    await SceneManager.instance.reInit(timelineContainerIdOrIndex);
    PubSub.publish('onTimelineLoadFinished', {});
  }

  /**
   * Returns bounding box of active scene, or undefined if scene is not loaded.
   */
  getBoundingBoxOfActiveScene() {
    const activeScene = this.getActiveScene();
    return activeScene ? activeScene.getGeodataBounderies() : undefined;
  }

  /**
   * @description Returns the current active scene
   */
  getActiveScene() {
    return SceneManager.instance.getActiveScene();
  }

  /**
   * Returns true if SkyView's scene is active, also as the CAD viewer
   */
  isSkyviewSceneActive() {
    return SceneManager.instance.isSceneActive('skyviewScene');
  }

  /**
   * Returns true if the CAD viewer is shown: SkyView's scene in a project without timeline data
   */
  isCadViewerActive() {
    return this.isSkyviewSceneActive() && ViewSettings.instance.isCadViewer;
  }

  /**
   * Returns true if SkyView's scene shows the timeline data, so not as the CAD viewer
   */
  isTimelineViewActive() {
    return this.isSkyviewSceneActive() && !ViewSettings.instance.isCadViewer;
  }

  /**
   * Returns true if the classic viewing mode shows the classic view
   */
  isClassicViewActive(view: ClassicView) {
    return (
      this.isTimelineViewActive() &&
      !ViewSettings.instance.isFlexible &&
      ViewSettings.instance.classicView === view
    );
  }

  private isOrthoCameraActive() {
    return cameraManager().getActiveCamera()?.cameraType === CameraType.OrthographicCamera;
  }

  /**
   * Returns true if the timeline data is viewed straight down, with the orthographic camera.
   */
  isOrthoViewActive() {
    return this.isTimelineViewActive() && this.isOrthoCameraActive();
  }

  /**
   * Returns true if the view is straight down with an orthographic camera: any ortho view,
   * including the CAD viewer's.
   */
  isTopDownViewActive() {
    return this.isOrthoViewActive() || (this.isCadViewerActive() && this.isOrthoCameraActive());
  }

  /**
   * Returns true if the orthophoto is shown in an ortho view.
   */
  isOrthophotoInOrthoViewActive() {
    const scene = this.getActiveScene();
    return (
      scene instanceof SkyviewScene &&
      scene.isOrthographicCameraActive() &&
      scene.isOrthophotoVisible()
    );
  }

  /**
   * Returns true if the timeline data is viewed in perspective.
   */
  isPerspectiveViewActive() {
    return this.isTimelineViewActive() && !this.isOrthoCameraActive();
  }

  isWmsMapAvailable() {
    return this.isOrthoViewActive() || (this.isCadViewerActive() && this.isOrthoCameraActive());
  }

  /**
   * @description Returns the screen position of the said coordinate in relation to the camera if orhtoscene is active
   * @returns {object}
   */
  setGpsLocation(lat: number, lng: number, alt = 0) {
    this.wgs84Position = { lat, lng, alt };
    if (lat === undefined || lng === undefined) {
      console.error('Invalid coordinate, needs to contain lat, long values', this.wgs84Position);
      this.wgs84Position = undefined;
      return;
    }

    return this.calculateScreenPositionRatio();
  }

  getActiveCamera() {
    return cameraManager().getActiveCamera();
  }

  checkLocationOutofBounds(latitude: number, longitude: number) {
    if (!this.isOrthoViewActive()) {
      return false;
    }

    const scene = this.getActiveScene();
    if (scene instanceof SkyviewScene) {
      const boundries = scene.getWgs84Boundries();
      if (boundries) {
        // This is a tolerance level of about 100 meters around the area.
        // See https://gis.stackexchange.com/questions/8650/measuring-accuracy-of-latitude-and-longitude/8674#8674
        // for a more detailed explanation
        const tolerance = 0.001;
        if (
          longitude > boundries.east + tolerance ||
          longitude < boundries.west - tolerance ||
          latitude > boundries.north + tolerance ||
          latitude < boundries.south - tolerance
        ) {
          (window as any).iosCallbacks?.pushLocationOutOfBounds?.();
          return false;
        }
      }
    }

    return true;
  }

  calculateScreenPositionRatio() {
    if (!this.wgs84Position) {
      return { x: 0, y: 0 };
    }

    const projection = CoordinateManager.getInstance().getProjectProjection();
    const swerefCoords = CoordinateManager.getInstance().convertCoords(
      this.wgs84Position.lng,
      this.wgs84Position.lat,
      'wgs84',
      projection,
    );
    const localCoords = CoordinateManager.getInstance().projectToLocal(
      swerefCoords.x,
      swerefCoords.y,
    );

    const p = new THREE.Vector3(
      localCoords.x,
      localCoords.y,
      this.wgs84Position.alt - CoordinateManager.getInstance().getMAS(),
    );
    const vector = p.project(cameraManager().getActiveCamera()?.getTHREECamera()!);

    return {
      x: ((vector.x + 1) / 2) * 100,
      y: (-(vector.y - 1) / 2) * 100,
    };
  }

  updateScreenRatio() {
    const newScreenRatio = this.calculateScreenPositionRatio();

    if (!this.updatedScreenRatio) {
      this.updatedScreenRatio = newScreenRatio;
    }

    if (
      !(
        newScreenRatio.x === this.updatedScreenRatio.x &&
        newScreenRatio.y === this.updatedScreenRatio.y
      )
    ) {
      this.updatedScreenRatio = this.calculateScreenPositionRatio();
    }
  }
  /**
   * @description Returns true if the geodata is available
   * @returns {boolean}
   */
  is3DSceneAvailable() {
    return ProjectGeodataManager.instance().hasScanData();
  }

  /**
   * @description Returns true if the geodata is available
   * @returns {boolean}
   */
  isOrthoSceneAvailable() {
    return ProjectGeodataManager.instance().hasOrthotileData();
  }

  /**
   * @description Returns true if point cloud is available
   * @returns {boolean}
   */
  isPointCloudSceneAvailable() {
    return ProjectGeodataManager.instance().hasCesiumPointCloudData();
  }

  resizeCanvas(width: number, height: number) {
    if (
      isDefined(this.renderer) &&
      isDefined(this.css2dRenderer) &&
      isDefined(this.css3dRenderer)
    ) {
      this.renderer.autoClear = false;
      this.renderer?.setSize(width, height);
      this.css2dRenderer?.setSize(width, height);
      this.css3dRenderer?.setSize(width, height);
      this.updateAspectRatio(width, height);
    }
    this.animate();
  }

  isLoading() {
    const activeScene = SceneManager.instance.getActiveScene();
    const isLoading = activeScene && activeScene.isLoading();

    !this.pushLoadingState &&
      (this.pushLoadingState = setTimeout(() => {
        (window as any).iosCallbacks?.pushLoadingState?.(isLoading);
        this.pushLoadingState = undefined;
      }, 500));
    return isLoading;
  }

  toggleValue(value: string) {
    // eslint-disable-next-line no-prototype-builtins
    if (!stateValues.hasOwnProperty(value)) {
      console.error("There is configuration value named '", value, "', ignoring toggle.");
    } else {
      stateValues[value] = !stateValues[value];
    }
  }

  animate() {
    const activeScene = SceneManager.instance.getActiveScene();
    if (!activeScene) {
      return;
    }

    // Ensures that the active camera projection is valid
    // A VERY small overhead to ensure a valid camera
    this.updateAspectRatio(
      this.canvas?.width ?? window.innerWidth,
      this.canvas?.height ?? window.innerHeight,
    );

    activeScene.animate();

    TWEEN.update();

    this.render();

    this.updateScreenRatio();
  }

  updateUI(e: THREE.BaseEvent<'updateUI'>) {
    this.dispatchEvent({ type: 'updateUI' });
  }

  setOcclusionCulling(state: boolean) {
    this.occlusionCulling = state;

    (window as any).iosCallbacks?.pushOcclusionState?.(this.occlusionCulling);
  }

  getOcclusionCulling() {
    return this.occlusionCulling;
  }

  focusOnSelectedAsset() {
    cameraManager().getActiveController()?.focusOnAsset(AssetManager.instance.getSelected());
  }

  focusOnGeodata() {
    const bb = this.getActiveScene()?.getGeodataBounderies();
    if (bb) {
      cameraManager().getActiveController()?.focusOnBox(bb);
    }
  }

  render() {
    renderActiveScene(this.renderer, this.css2dRenderer, this.css3dRenderer, this.baseScene, {
      occlusionCulling: this.occlusionCulling,
    });

    setTimeout(() => {
      this.renderer && this.renderer.renderLists.dispose();
    }, 0);

    this.FPSCounter.tick();
  }

  clearCustom() {
    this.deactivated = true;
    if (this.resizeObserver) {
      if (this.resizeObserverTarget) {
        this.resizeObserver.unobserve(this.resizeObserverTarget);
      } else {
        this.resizeObserver.disconnect();
      }
    }

    SceneManager.instance.clearCustom();

    // Clear all cached texture
    TextureManager.instance.clearCustom();

    // Clear the stored wgs84 coordinates
    cameraManager().clearCustom();

    this.animateInterval && clearInterval(this.animateInterval);
    this.updateUIInterval && clearInterval(this.updateUIInterval);

    this.renderer?.dispose();
    delete this.renderer;

    InputHandler.instance.disable();
    InputHandler.instance.removeEventListener('animate', this.animateCallback!);
    InputHandler.instance.removeEventListener('updateUI', this.updateUICallback!);
    InputHandler.instance.abort();

    this.removeEventListener(wmsCanvasRenderedEvent, this.updateWmsTexture.bind(this));

    PointCloudHandle.instance.clearCustom();
    AssetManager.instance.clearCustom();

    this.asbuiltGeometry.clear();
  }

  /**
   *  This loads the css/webgl containers.
   *  It retries every 100 ms.
   *
   *  TODO: Try to identify and handle execution order to ensure canvas exist when initiating SkyView.
   */
  async fetchTopViewerContainer(): Promise<HTMLDivElement> {
    return new Promise((resolve, reject) => {
      void waitForElement<HTMLDivElement>('#css3drenderercontainer', 5000)
        .then(resolve)
        .catch(reject);
    });
  }

  private updateAspectRatio(width: number, height: number) {
    cameraManager().setAspectRatio(width, height);
    this.distributeEvent({
      type: 'canvasResize',
      width: width,
      height: height,
    });
  }

  private updateWmsTexture(
    event: THREE.BaseEvent<'wmsCanvasRendered'> & SkyViewEventMap['wmsCanvasRendered'],
  ) {
    const payload:
      | {
          canvas: HTMLCanvasElement;
          extents: Box2;
        }
      | undefined = event.data;

    WmsState.instance.setCanvas(payload?.canvas, payload?.extents ?? new Box2());
  }

  /**
   * As the SceneManager and current scene is not part of the SkyView base scene,
   * this function allows to distribute events to the base scene and the active scene in the viewer.
   * @param event - The event to distribute.
   */
  distributeEvent<T extends keyof SkyViewEventMap>(event: { type: T } & SkyViewEventMap[T]) {
    this.dispatchEvent(event);
  }
}

export function renderActiveScene(
  renderer: WebGLRenderer | undefined,
  css2dRenderer: CSS2DRenderer | undefined,
  css3dRenderer: CSS3DRenderer | undefined,
  baseScene: Scene = new Scene(),
  options?: { occlusionCulling?: boolean; clearColor?: number; hideAssets?: boolean },
) {
  const camera = cameraManager().getActiveCamera()?.getTHREECamera();
  const activeScene = SceneManager.instance.getActiveScene();
  if (!camera || !renderer || !isDefined(css2dRenderer) || !activeScene) {
    return;
  }

  // https://github.com/mrdoob/three.js/issues/5278#issuecomment-117444278
  renderer.state.reset();
  renderer.setClearColor(options?.clearColor ?? SceneManager.instance.clearColor, 1);

  renderer.clear();

  activeScene.render(renderer, css2dRenderer);

  if (!options?.occlusionCulling) {
    renderer.clearDepth();
  }

  if (!options?.hideAssets) {
    AssetManager.instance.render(renderer, css2dRenderer, camera);
  }
  renderer.render(baseScene, camera);

  // Render texts last.
  if (!options?.hideAssets) {
    AssetManager.instance.renderText(css3dRenderer, camera);
  }
}
